import {
  type ProspectContactDto,
  useEventCreateEvent,
  useLoginWhoAmI,
  useSalesGetSalesFolks,
  useSalesSetOwner,
} from "@crowdcoursing/api/openapi";
import { skipToken, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { useSalesBubbleId } from "~hooks/useSalesBubbleId";
import { useReassignOpenTasksPrompt } from "~ui/pages/SalesPortal/useReassignOpenTasksPrompt";
import { useToast } from "~hooks/useToast";
import { Route } from "~services/router/Route";
import { useContactModal } from "~ui/pages/SalesPortal/useContactModal";
import { useInteractionModal } from "~ui/pages/SalesPortal/useInteractionModal";
import { useNoteModal } from "~ui/pages/SalesPortal/useNoteModal";
import { useProspectDetails } from "~ui/pages/SalesPortal/useProspectDetails";
import { useTaskModal, type UseTaskModalOptions } from "~ui/pages/SalesPortal/useTaskModal";
import { isAdminOfBubble } from "~utils/getAdminBubbleIds";

export interface UseProspectDetailsSlideoutOptions {
  onSalesTaskUpdated?: UseTaskModalOptions["onTaskUpdated"];
}

const tOwnerChangeSuccess = "Prospect owner updated";
const tOwnerChangeError = "Failed to change prospect owner";

export const useProspectDetailsSlideout = (prospectId: string, options?: UseProspectDetailsSlideoutOptions) => {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const salesBubbleId = useSalesBubbleId();
  const { offerOpenTaskReassign, reassignOpenTasksDialog } = useReassignOpenTasksPrompt();
  const { data: whoAmI } = useLoginWhoAmI({});
  const canChangeOwner = isAdminOfBubble(whoAmI?.userDto?.bubbleRoles, salesBubbleId);

  const { addProspectToDeal, deals, isAddingProspectToDeal, isLoadingDeals, prospect } = useProspectDetails(prospectId);

  const primaryContactDto = prospect?.contacts?.find((contact) => contact.isPrimary);
  const primaryEmail = primaryContactDto?.email ?? null;
  const primaryPhone = primaryContactDto?.phone ?? null;
  const primaryContact = primaryContactDto
    ? {
        firstName: primaryContactDto.firstName ?? undefined,
        id: primaryContactDto.id,
        isPrimary: primaryContactDto.isPrimary,
        lastName: primaryContactDto.lastName ?? undefined,
        roleTitle: primaryContactDto.roleTitle ?? undefined,
      }
    : undefined;

  const { data: salesPersonnel = [] } = useSalesGetSalesFolks(canChangeOwner ? {} : skipToken);
  const { isPending: isUpdatingOwner, mutateAsync: setOwner } = useSalesSetOwner();
  const ownerOptions = useMemo(
    () =>
      salesPersonnel.flatMap((user) =>
        user.id ? [{ firstName: user.firstName, id: user.id, lastName: user.lastName }] : [],
      ),
    [salesPersonnel],
  );

  // Modal hooks
  const contactModal = useContactModal(prospectId);
  const noteModal = useNoteModal(prospectId);
  const interactionModal = useInteractionModal(prospectId);
  const taskModal = useTaskModal(prospectId, { onTaskUpdated: options?.onSalesTaskUpdated });

  // Create a unified modal interface
  const modals = {
    ...contactModal,
    closeInteractionModal: interactionModal.closeAddModal,
    closeNoteModal: noteModal.closeAddModal,
    closeTaskModal: taskModal.closeAddModal,
    handleAddInteraction: interactionModal.handleAddInteraction,
    handleAddNote: noteModal.handleAddNote,
    handleCreateTask: taskModal.handleCreateTask,
    openInteractionModal: interactionModal.openAddModal,
    openNoteModal: noteModal.openAddModal,
    openTaskModal: taskModal.openAddModal,
  };

  // Event creation mutation
  const { mutateAsync: createEvent } = useEventCreateEvent({
    onError: (error) => {
      console.error("Error creating event:", error);
      toast.error("Failed to create event. Please try again.");
    },
    onSuccess: (response) => {
      toast.success("Event created successfully!");
      navigate(Route.eventView({ eventId: response.id ?? "" }));
    },
  });

  // Handler for scheduling a meeting
  const handleScheduleMeeting = async () => {
    if (!prospect) {
      return;
    }

    await createEvent({
      body: {
        bubbleId: "c0524501-ff1c-4b76-b384-1317e342aa48",
        description:
          "Make sure to create a meeting time and RSVP so that it will show up on your schedule. You can also create a google meets and add it as a Resource here, as well. If you'd like to invite the person to this event, make sure to change the description and add an event photo before doing so.",
        isPublic: false,
        isSearchable: false,
        name: `CrowdCoursing Demo with ${prospect.name}`,
      },
    });
  };

  const handleOwnerChange = useCallback(
    async (userId: string) => {
      if (!salesBubbleId || !prospectId || userId === prospect?.ownerUserId) {
        return;
      }

      try {
        await setOwner({
          pathParams: { bubbleId: salesBubbleId, id: prospectId, userId },
        });
        await queryClient.invalidateQueries({ queryKey: ["api", "v1", "sales"] });
        toast.success(tOwnerChangeSuccess);
        await offerOpenTaskReassign(prospectId, userId);
      } catch {
        toast.error(tOwnerChangeError);
      }
    },
    [offerOpenTaskReassign, prospect?.ownerUserId, prospectId, queryClient, salesBubbleId, setOwner, toast],
  );

  // Handle contact card click
  const handleContactCardClick = () => {
    if (primaryContact?.id) {
      const contactData: ProspectContactDto = {
        createdAt: "",
        email: primaryEmail || undefined,
        firstName: primaryContact.firstName,
        id: primaryContact.id,
        isPrimary: primaryContact.isPrimary,
        lastName: primaryContact.lastName,
        phone: primaryPhone || undefined,
        prospectId: prospectId,
        roleTitle: primaryContact.roleTitle,
        updatedAt: "",
      };

      contactModal.openEditModal(contactData);
    }
  };

  return {
    addProspectToDeal,
    canChangeOwner,
    contactModal,
    deals,
    handleContactCardClick,
    handleOwnerChange,
    handleScheduleMeeting,
    interactionModal,
    isAddingProspectToDeal,
    isLoadingDeals,
    isUpdatingOwner,
    modals,
    noteModal,
    ownerOptions,
    primaryContact,
    primaryEmail,
    primaryPhone,
    prospect,
    reassignOpenTasksDialog,
    taskModal,
  };
};
